import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import utc from 'dayjs/plugin/utc';
import { ParsedUrlQuery } from 'querystring';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { getI18n } from 'react-i18next';
import { option } from '../components/CustomFieldComponent';
import { FULL_QUERY_SUPPORTED_PATH_LIST } from '../constants/appUrls';
import { DetailedUser, Field, KeysMatching, Tab } from '../model/types';
import { isAdminOrganizationFieldsPage, isAdminTicketFieldsPage, isAdminUserFieldsPage } from './routeHelper';

export const replaceItemAtIndexWithGivenParam = (
    arr: any[],
    givenParam: string,
    newValue: Record<string, unknown>
): any[] => {
    const atIndex = arr.findIndex(({ key }) => key === givenParam);
    const findTab = arr.find(({ key }) => key === givenParam);
    const editedTab = { ...findTab, ...newValue };
    const newOpenTabs = [...arr.slice(0, atIndex), editedTab, ...arr.slice(atIndex + 1)];
    return newOpenTabs;
};

export const replaceItemAtIndex = (arr: any[], atIndex: number, newItem: unknown): any[] => {
    const newArray = [...arr.slice(0, atIndex), newItem, ...arr.slice(atIndex + 1)];

    return newArray;
};

type value<T> = T extends number ? T[] : T | T[];
export const removeEmptyFields = (
    previousValue: Record<string, value<string | number>>,
    [key, value]: [string, value<string | number>]
): Record<string, any> => {
    return !value || value.length === 0 ? previousValue : { ...previousValue, [key]: value };
};

export const isRelatedTabAlreadyOpen = (tabs: Tab[], concernedTabKey: string): boolean => {
    return tabs.map(({ key }) => key).includes(concernedTabKey);
};

// ref: https://gist.github.com/codeguy/6684588#gistcomment-3426313
export const slugify = (...args: (string | number)[]): string => {
    const value = args.join(' ');

    return value
        .normalize('NFD') // split an accented letter in the base letter and the acent
        .replace(/[\u0300-\u036f]/g, '') // remove all previously split accents
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9- ]/g, '') // remove all chars not letters, numbers, spaces and dash (to be replaced)
        .replace(/-{2,}/g, '-') // replace all two or more consecutive '-' chars with one '-'
        .replace(/\s+/g, '-'); // separator
};

export const slugifyUnderscore = (...args: (string | number)[]): string => {
    const value = args.join(' ');

    return value
        .normalize('NFD') // split an accented letter in the base letter and the acent
        .replace(/[\u0300-\u036f]/g, '') // remove all previously split accents
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_ ]/g, '') // remove all chars not letters, numbers, spaces and dash (to be replaced)
        .replace(/_{2,}/g, '_') // replace all two or more consecutive '-' chars with one '-'
        .replace(/\s+/g, '_'); // separator
};

export const getCustomFieldKeyPath = (query: ParsedUrlQuery): string => (query?.adminOption as string).split('-')[0][0];

export const createCustomFieldKey =
    (customFieldKeyPath: string) =>
    (value: string | number): string =>
        value ? `${customFieldKeyPath}u.${slugifyUnderscore(value)}` : '';

export const slugifyPhoneNumber = (phoneNumber: string): string => {
    // remove all special chars, letters (to be replaced)
    const slugifiedPhoneNumber =
        phoneNumber.length === 0 ? phoneNumber : phoneNumber.replace(/[a-zA-Z -/:-@˜ˆ[-`{-~]/g, '');

    const withPlus = phoneNumber.length === 0 ? slugifiedPhoneNumber : `+${slugifiedPhoneNumber}`;
    return withPlus;
};

export const isSSR = (): boolean => typeof window === 'undefined';

export const getTicketIdFromKey = (key: string): string => key?.split('-')?.[1];

export const getSideMenuInitialSelectedKey = (pathname: string): string[] =>
    pathname.startsWith('/filters')
        ? ['2']
        : pathname.startsWith('/admin')
        ? ['4']
        : pathname.startsWith('/organizations')
        ? ['3']
        : pathname.startsWith('/tickets')
        ? ['0']
        : pathname.startsWith('/users')
        ? ['5']
        : pathname.startsWith('/reports')
        ? ['6']
        : pathname.startsWith('/advanced-reports')
        ? ['7']
        : ['1'];

export const isAuthenticationRelatedPage = (pathname: string): boolean =>
    pathname.startsWith('/auth') || pathname === '/reset-password';

export const sleep = (ms: number): Promise<any> => new Promise((r) => setTimeout(r, ms));

export const mockServiceRequestByTimeout = async (ms = 500): Promise<unknown> => {
    const randomNumber = Math.floor(Math.random() * 4 + 1);
    await sleep(ms);
    if (randomNumber === 4) return Promise.reject(new Error('Sth went wrong'));
    return Promise.resolve('Succes');
};

export const sortTypeObjectMap = { descending: 'descending', ascending: 'ascending' } as const;
const sortTypeObject = { desc: 'descending', asc: 'ascending' } as const;
export type sortType = (typeof sortTypeObject)[keyof typeof sortTypeObject];

export const by =
    <T>(key: KeysMatching<T, number>) =>
    (sortType: sortType) =>
    (a: T, b: T): number => {
        if (sortType === sortTypeObjectMap.descending) return <any>b[key] - <any>a[key];
        if (sortType === sortTypeObjectMap.ascending) return <any>a[key] - <any>b[key];
    };

//TODO cagri: add locale support
export const getFormattedDateWithDayjs = (time: dayjs.ConfigType): string =>
    time ? dayjs(time).format('DD-MM-YYYY') : 'noDate';
export const getFormattedDateWithHourWithDayjs = (time: dayjs.ConfigType): string =>
    time ? dayjs(time).format('DD-MM-YYYY HH:mm') : 'noDate';
export const getCurrentYear = (): number => new Date().getFullYear();

export const getAntdUploadElement = (id: string): HTMLInputElement =>
    document.getElementById(id).firstElementChild.firstElementChild.firstElementChild
        .firstElementChild as HTMLInputElement;

export const authNotRequiredPages = ['/forgot-password', '/login', '/register', '/reset-password'];

export const twoDigitNumber = (value: number): string => {
    const leftDigit = value >= 10 ? value.toString()[0] : '0';
    const rightDigit = value >= 10 ? value.toString()[1] : value.toString();
    return `${leftDigit}${rightDigit}`;
};

const isDebug = !isSSR() && new URLSearchParams(window.location.hash.substr(1)).get('debug') != null;
export const hasDebugHash = (): boolean => isDebug;

type Record<K extends keyof any, T> = {
    [P in K]: T;
};

export const getDirtyFields = <T>(
    dirtyFields: FieldNamesMarkedBoolean<T>,
    formData: T
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
): { [K in keyof typeof dirtyFields]: T[K] }[keyof T] =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    Object.keys(dirtyFields).reduce((acc, label) => ({ ...acc, [label]: formData[label] }), {}) as {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        [K in keyof typeof dirtyFields]: T[K];
    }[keyof T];

const notValidEmailAddressList = ['example.com', 'missing-name.example.com', 'deleted.example.com'];
export const getMaskedEmailForUI = (value: string, showEmpty = false): string => {
    if (!value) return '-';

    const isSafeEmail = !notValidEmailAddressList.includes(value.split('@')?.[1]);

    if (isSafeEmail) return value;

    if (!showEmpty && value.includes('@example.com')) return `+${value.split('@')[0]}`; // to show end-user phone number when it is created w/ phone(and w/out email) for the first time

    return '';
};

export const getUserCredentialsToShow = (user: DetailedUser, isNameFirst?: boolean) => {
    if (!user) return '-';
    if (isNameFirst) return user.fullName ?? user.email ?? user.phone;
    return user.email ?? user.fullName ?? user.phone;
};

export const getSafeUserNameForRequest = (value: string): string => {
    if (!value) return value;
    const safeUserName = value.includes('+') ? value.replace('+', '%2B') : value;
    return safeUserName;
};

type pipableFunction = (param: any) => any;

export const pipe =
    (...funcs: pipableFunction[]): any =>
    <T>(firstParam: T): ReturnType<typeof pipe> =>
        funcs.reduce((res: T, func): ReturnType<typeof func> => func(res), firstParam);

export const getParentPage = (asPath: string): string => {
    const url = asPath.split('/').slice(0, -1).join('/');
    if (FULL_QUERY_SUPPORTED_PATH_LIST.includes(url)) return `${url}?page=1&size=10&active=1&sort=name,asc`;
    return url;
};

export const selectOptionsFactory: <T extends unknown>(
    labelKey: keyof T,
    valueKey: KeysMatching<T, string | number>
) => (object: T) => any = (labelKey, valueKey) => (object) => {
    const options: option[] = [];
    if ((object as Field)?.type === 'MULTI_USER') {
        const labelArray = (object as Field)?.[labelKey as 'userFriendlyValue']?.split(',');
        const valueArray = (object as Field)?.[valueKey as 'serializedValue']?.split(',');

        labelArray.forEach((label, index) => {
            options.push({
                label: getMaskedEmailForUI(label),
                value: valueArray?.[index]
            });
        });
        return options;
    }

    return {
        label: getMaskedEmailForUI(object?.[labelKey] as any),
        value: object?.[valueKey]
    };
};

export const byObjectKey: <T extends Record<string, any>, MV>(
    searchValue: MV,
    searchOnKey: KeysMatching<T, MV>
) => (object: T) => boolean = (matchValue, matchKey) => (object) => object[matchKey] === matchValue;

export const nonNullishValue = (value: unknown): boolean => value !== null && value !== undefined;
export const nullishValue = (value: unknown): boolean => value === null || value === undefined;

// export const emptyValue = (value: unknown): boolean => {
//     if (value === undefined || value === null) return true;
//     if (typeof value === 'string') return value.trim().length === 0;
//     if (typeof value === 'object') return Object.keys(value).length === 0;
//     return false;
// };

export const capitalizeFirstLetter = (key: string): string => {
    return key.charAt(0).toUpperCase() + key.slice(1);
};

export const getCustomFieldEndPointByUrl = (asPath: string, id: number | string) => {
    const fullUrl = isAdminOrganizationFieldsPage(asPath)
        ? `/organization-fields/${id}`
        : isAdminUserFieldsPage(asPath)
        ? `/user-fields/${id}`
        : isAdminTicketFieldsPage(asPath)
        ? `/fields/${id}`
        : null;
    return fullUrl;
};

export const guessTimeZone = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    return dayjs.tz.guess();
};

export function humanFileSize(size: number) {
    if (size === 0) return size;
    const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export const localizedSearchTermForApi = (searchTerm: string): string => {
    if (nullishValue(searchTerm)) return searchTerm;
    const localizedSearchTerm = searchTerm
        .trim()
        .replaceAll(/\s+/g, ' ')
        .replaceAll('İ', 'i')
        .toLocaleLowerCase(getI18n().language?.toLowerCase());
    //encodeURIComponent(searchTerm) sanitize to searchTerm
    // return encodeURIComponent(localizedSearchTerm);
    return localizedSearchTerm;
};

// useAutoCompleteFieldOptions - /field-values
// useTagSearch - /field-values
// useAutocompleteUserSearch - /field-values
// useAutocompleteSearch - /field-values
// useAutocompleteSearchByFieldKey - /field-values
// getMentionableUsers - /field-values
// useCustomerSearch - /customers
// useOrganizationSearch - /organizations
// useTicketSearch - /tickets/search
